import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "@/contexts/AuthContext";
import MeetingDevicePreview from "@/components/MeetingDevicePreview";
import MeetingPeerMesh from "@/lib/MeetingPeerMesh";

function RemoteParticipant({ peer }) {
  const video = useRef(null);
  const [blocked, setBlocked] = useState(false);
  useEffect(() => {
    const element = video.current;
    element.srcObject = peer.stream;
    element.play().then(() => setBlocked(false)).catch(() => setBlocked(true));
    return () => { element.srcObject = null; };
  }, [peer.stream]);
  return <article className="space-y-2 rounded border p-2">
    <video ref={video} autoPlay playsInline className="aspect-video w-full rounded bg-slate-900" />
    <p className="text-sm">{peer.name} · {peer.state}</p>
    {blocked && <button type="button" className="rounded border px-3 py-1"
      onClick={() => video.current.play().then(() => setBlocked(false)).catch(() => setBlocked(true))}>Play participant audio/video</button>}
  </article>;
}

export default function MeetingMediaRoom({ eventId, guestToken }) {
  const mesh = useRef(null);
  const localTracks = useRef({ audio: null, video: null });
  const alive = useRef(false);
  const [open, setOpen] = useState(false);
  const [peers, setPeers] = useState([]);
  const [error, setError] = useState("");
  const [captureKey, setCaptureKey] = useState(0);
  const onTracks = useCallback((tracks) => {
    localTracks.current = tracks;
    mesh.current?.setTracks(tracks);
  }, []);
  function disconnect() {
    const current = mesh.current; mesh.current = null; current?.close();
    setOpen(false); setPeers([]); setCaptureKey((value) => value + 1);
  }
  useEffect(() => {
    alive.current = true;
    const leave = () => { mesh.current?.close(); mesh.current = null; };
    window.addEventListener("pagehide", leave);
    return () => {
      alive.current = false; leave(); window.removeEventListener("pagehide", leave);
    };
  }, []);
  async function connect() {
    if (mesh.current) return;
    setError("");
    const guest = Boolean(guestToken);
    const base = API + "/meetings/" + (guest ? "guest/" : "events/") + encodeURIComponent(eventId) + "/media";
    async function guestRequest(url, body, signal) {
      const response = await fetch(url, { method: "POST", credentials: "omit",
        cache: "no-store", referrerPolicy: "no-referrer", signal,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...body, session_token: guestToken }) });
      if (!response.ok) {
        const failure = new Error(response.status === 503 ? "Meeting audio and video are not enabled yet."
          : "Meeting connection unavailable. Check your invitation and try again.");
        failure.status = response.status; throw failure;
      }
      return response.json();
    }
    const read = async (after, signal) => guest ? guestRequest(base, { after }, signal)
      : (await axios.get(base, { params: { after }, signal })).data;
    const send = async (payload, signal) => {
      // Retry uncertain/conflicting writes with the SAME request UUID.
      for (let attempt = 0; attempt < 4; attempt += 1) {
        try {
          if (guest) return await guestRequest(base + "/signals", payload, signal);
          return (await axios.post(base + "/signals", payload, { signal })).data;
        } catch (failure) {
          const status = failure.status || failure.response?.status;
          if (signal.aborted || ![409, 429].includes(status) || attempt === 3) throw failure;
          await new Promise((resolve) => setTimeout(resolve, 250 * (attempt + 1)));
        }
      }
    };
    const current = new MeetingPeerMesh({
      read, send, rtcConfiguration: { iceServers: [] },
      onPeers: (value) => { if (alive.current) setPeers(value); },
      onError: () => {
        if (!alive.current) return;
        setError("The media connection stopped or is unavailable. Reconnect to try again.");
        if (mesh.current === current) disconnect();
      },
    });
    mesh.current = current; setOpen(true);
    await current.setTracks(localTracks.current);
    if (mesh.current === current) await current.start();
  }
  return <section className="space-y-3" aria-label="Meeting audio and video">
    <MeetingDevicePreview key={captureKey} onTracks={onTracks} sharing={open} />
    {error && <p role="alert" className="text-sm">{error}</p>}
    <div className="flex gap-2">
      <button type="button" disabled={open} onClick={connect} className="rounded border px-3 py-2">Connect audio &amp; video</button>
      <button type="button" disabled={!open} onClick={disconnect} className="rounded border px-3 py-2">Disconnect media</button>
    </div>
    <p className="text-xs text-slate-600">{open ? "Media session requested. Participant tiles show individual connection states." : "Media disconnected."}</p>
    <div className="grid gap-3 sm:grid-cols-2">{peers.map((peer) => <RemoteParticipant key={peer.id} peer={peer} />)}</div>
  </section>;
}
